import { caseActions } from "../store/case"
import { filterActions } from "../store/filter"
import { permissionsActions } from "../store/permissions"
import { userActions } from "../store/user"
import { notificationActions } from "../store/notifications"
import { systemActions } from "../store/system"
import store from "../store"
import { getCaseIdFromCasePayload } from "./caseUpdateHelpers"
import { setStorageItem } from "./script"
import { authorizationHeaders, developmentProxyUrl } from "./basicAuth"

const API_ROUTE = developmentProxyUrl(process.env.REACT_APP_API_URL)
const AUTH_REDIRECT_MESSAGE_KEY = 'authRedirectMessage'
const SESSION_EXPIRED_FALLBACK = 'Your session has expired. Please log in again.'
let inFlightSystemSettingsPromise = null
let systemSettingsSyncPromise = null
let unauthorizedRedirectInProgress = false
const latestQuickUpdateSequenceByKey = new Map()
const quickUpdateAbortControllerByKey = new Map()

function extractUnauthorizedMessage(resData) {
    const raw = resData?.message ?? resData?.errors ?? resData?.data?.message ?? resData?.data?.errors
    if (typeof raw === 'string' && raw.trim()) {
        return raw
    }
    if (Array.isArray(raw) && raw.length && typeof raw[0] === 'string') {
        return raw[0]
    }
    return SESSION_EXPIRED_FALLBACK
}

async function parseResponseJson(response) {
    try {
        return await response.json()
    } catch {
        return null
    }
}

function clearSessionAndRedirectToLogin(message) {
    if (unauthorizedRedirectInProgress) {
        return
    }
    unauthorizedRedirectInProgress = true
    try {
        sessionStorage.setItem(AUTH_REDIRECT_MESSAGE_KEY, message)
    } catch {
        // ignore
    }
    try {
        store.dispatch(userActions.setToken(null))
        store.dispatch(userActions.setUser(null))
    } catch {
        // ignore
    }
    localStorage.clear()
    window.location.assign('/')
}

async function handleUnauthorizedResponse(response) {
    const resData = await parseResponseJson(response)
    const message = extractUnauthorizedMessage(resData)
    clearSessionAndRedirectToLogin(message)
    return { errorMessage: message, status: 401 }
}

function abortQuickUpdateForKey(key) {
    if (!key) {
        return null
    }
    const previous = quickUpdateAbortControllerByKey.get(key)
    if (previous) {
        try {
            previous.abort()
        } catch {
            // ignore
        }
    }
    const controller = new AbortController()
    quickUpdateAbortControllerByKey.set(key, controller)
    return controller
}

function releaseQuickUpdateController(key, controller) {
    if (!key || !controller) {
        return
    }
    if (quickUpdateAbortControllerByKey.get(key) === controller) {
        quickUpdateAbortControllerByKey.delete(key)
    }
}

/** Case id from `/cases/:id/...` or `/work-queues/:slug/:id/...` for Redux merge when dispatch was omitted. */
function extractCaseIdForCaseDataMerge(url) {
    if (!url || typeof url !== "string") {
        return null
    }
    let m = url.match(/\/cases\/([^/?#]+)/)
    if (!m) {
        m = url.match(/\/work-queues\/[^/]+\/([^/?#]+)/)
    }
    if (!m) {
        return null
    }
    const n = Number(m[1])
    return Number.isNaN(n) ? null : n
}

/**
 * When a caller did not pass `dispatch`, still merge `case_data` / work-queue rows into Redux if the
 * request targets the same case as `activeCase` (e.g. work-queue drawer open on that case).
 */
function resolveCaseDataMergeDispatch(caseId, explicitDispatch) {
    if (typeof explicitDispatch === "function") {
        return explicitDispatch
    }
    if (caseId == null || caseId === "") {
        return null
    }
    let activeCase
    try {
        activeCase = store.getState()?.case?.activeCase
    } catch {
        return null
    }
    const activeId = getCaseIdFromCasePayload(activeCase)
    if (activeId == null) {
        return null
    }
    if (Number(activeId) === Number(caseId)) {
        return store.dispatch
    }
    return null
}

// const API_ROUTE = '/api'

function persistLastGlobalSync(lastGlobalSync) {
    if (lastGlobalSync == null || lastGlobalSync === '') {
        return
    }
    localStorage.setItem('last_global_sync', String(lastGlobalSync))
}

function requestSystemSettings(token, signal) {
    return genericFetchBearer(
        token,
        `${API_ROUTE}/system-settings`,
        null,
        null,
        null,
        null,
        signal
    )
}

async function setSystemSettings(url, last_global_sync) {
    if (!url) {
        return
    }

    if (url.includes('system-settings')) {
        persistLastGlobalSync(last_global_sync)
        return
    }

    if (last_global_sync == null || last_global_sync === '') {
        return
    }

    const targetSync = String(last_global_sync)
    const lastSync = localStorage.getItem('last_global_sync')
    if (lastSync != null && lastSync === targetSync) {
        return
    }

    if (systemSettingsSyncPromise) {
        return systemSettingsSyncPromise
    }

    systemSettingsSyncPromise = (async () => {
        try {
            const systemSettings = await getSystemSettings(localStorage.getItem('token'))
            if (systemSettings && !systemSettings.errorMessage) {
                setStorageItem('settings', systemSettings)
                persistLastGlobalSync(targetSync)
                store.dispatch(systemActions.bumpSettingsRevision())
            }
        } catch (error) {
            console.error('Failed to sync system settings', error)
        } finally {
            systemSettingsSyncPromise = null
        }
    })()

    return systemSettingsSyncPromise
}

async function setNewUserPermissions(url, last_permission_update, dispatch) {

    if (url && !url.includes('user/me')) {
        let lastPermission = localStorage.getItem('last_permission_update')

        if (!lastPermission || (lastPermission !== last_permission_update)) {
            const user = await getUser(localStorage.getItem('token'))  
            setStorageItem('user', user)
            localStorage.setItem('last_permission_update', last_permission_update)

            if (dispatch) {
                dispatch(userActions.setUser(user))
                // let editPermissionsObj = {}
                // for (const [key, value] of Object.entries(user.permissions)) {               
                //     if (key.includes('_edit')) {
                //         editPermissionsObj[key] = value
                //     }
                // } 
                dispatch(permissionsActions.setEditPermissions(user.permissions))
            }
        } 
    }  
}

const genericFetch = async (url, method, body, additionalHeader) => { // used for login
    const options = {
        method: method || 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...authorizationHeaders(),
        },
        
    }

    if (additionalHeader) {
        options.headers['x-pdf-header'] = additionalHeader
    }

    if (body) {
        options.body = JSON.stringify(body)
    }

    const response = await fetch(url, options)
    if (!response.ok) {
        const resData = await response.json();
        return {
            errorMessage: 'Something went wrong',
            response: resData,
            status: 500
        }
    } else {
        const resData = await response.json();        
        
        return resData.data
    }
}

const genericFetchBearer = async (token, url, method, body, dispatch, caseId, signal, isQuickUpdate = false, mergeOptions = {}) => { // used for pretty much anything else
    // caseid is used for workq batch updates!
    const {
        allowImplicitMerge = true,
        quickUpdateMeta = null,
        quickUpdateAutoMergeCaseData = false,
    } = mergeOptions
    const options = {
        method: method || 'GET',
        signal: signal || null,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...authorizationHeaders(token),
        },
        
    }

    if (body) {
        options.body = JSON.stringify(body)
    }
    let response
    try {
        response = await fetch(url, options)
    } catch (error) {
        if (error?.name === 'AbortError') {
            return isQuickUpdate
                ? { aborted: true, staleResponse: true }
                : null
        }
        throw error
    }

    // return null
    if (!response.ok) {
        if (response.status === 401) {
            return handleUnauthorizedResponse(response)
        }
        const resData = await response.json();
        if (resData?.confirmation || resData?.data?.confirmation) {
            return {
                confirmation: resData.confirmation ?? resData.data.confirmation,
                confirmation_type: resData.confirmation_type ?? resData.data?.confirmation_type,
                status: response.status,
            }
        }
        const errors = resData?.data?.errors ?? resData?.errors

        if (errors) {
            const errorMessage = (
                Array.isArray(errors) ||
                typeof errors === 'string' ||
                errors?.errors ||
                errors?.message
            ) ? errors : { errors }

            return {
                errorMessage,
                status: response.status
            }
        }
        return {
            errorMessage: 'Something went wrong',
            status: response.status
        }
    } else {
        const resData = await response.json();

        setSystemSettings(url, resData.last_global_sync) 
        setNewUserPermissions(url, resData.last_permission_update, dispatch)

        const hasQuickUpdateGuard = Boolean(
            isQuickUpdate &&
            quickUpdateMeta?.staleGuardKey &&
            quickUpdateMeta?.staleGuardSequence != null
        )
        const latestSequenceForKey = hasQuickUpdateGuard
            ? latestQuickUpdateSequenceByKey.get(quickUpdateMeta.staleGuardKey)
            : null
        const isStaleQuickUpdateResponse = hasQuickUpdateGuard && Number(latestSequenceForKey) !== Number(quickUpdateMeta.staleGuardSequence)
        const canImplicitlyMerge = allowImplicitMerge && !isQuickUpdate
        const needsAutoMerge =
            canImplicitlyMerge &&
            typeof dispatch !== "function" &&
            (resData.case_data || resData.work_queue_data)
        const needsQuickUpdateCaseMerge =
            isQuickUpdate &&
            quickUpdateAutoMergeCaseData &&
            typeof dispatch !== "function" &&
            Boolean(resData.case_data)
        const idFromUrlForMerge = (needsAutoMerge || needsQuickUpdateCaseMerge)
            ? extractCaseIdForCaseDataMerge(url)
            : null
        const mergeDispatch =
            typeof dispatch === "function"
                ? dispatch
                : idFromUrlForMerge != null
                    ? resolveCaseDataMergeDispatch(idFromUrlForMerge, null)
                    : null

        if (mergeDispatch && !isStaleQuickUpdateResponse) {
            if (resData.case_data) {
                mergeDispatch(caseActions.batchUpdate(resData.case_data))
            }
            if (resData.work_queue_data && (typeof dispatch === "function" || needsAutoMerge)) {
                mergeDispatch(
                    filterActions.batchUpdate({
                        data: resData.work_queue_data,
                        caseId: caseId ?? idFromUrlForMerge,
                    })
                )
            }
        }

        if (isQuickUpdate) {
            return {
                caseData: resData.case_data,
                data: resData.data,
                staleResponse: isStaleQuickUpdateResponse,
            }
        }
        return resData.data
    }
}

const genericFileFetch = async (token, url, method, body) => { // as the name implies, used for files
    const options = {
        method: method || 'GET',
        headers: {
            // 'Content-Type': 'application/json',
            'Accept': 'application/octet-stream',
            ...authorizationHeaders(token),
        },
        
    }

    if (body) {
        options.body = JSON.stringify(body)
    }
    const response = await fetch(url, options)
    if (!response.ok) {
        if (response.status === 401) {
            return handleUnauthorizedResponse(response)
        }
        const resData = await response.json();
        return {
            errorMessage: resData.data,
            status: response.status
        }
    } else {
        const resData = await response.blob();
        
        return resData
    }
}

const genericFetchMultipartBearer = async (token, url, method, body, dispatch) => {
    const options = {
        method: method || 'GET',
        headers: {
            // 'Content-Type': 'multipart/form-data',
            // 'Accept': 'multipart/form-data',
            ...authorizationHeaders(token),
        }
    }

    if (body) {
        // options.body = JSON.stringify(body)
        options.body = body
    }
    const response = await fetch(url, options)

    if (!response.ok) {
        if (response.status === 401) {
            return handleUnauthorizedResponse(response)
        }
        const resData = await response.json();
        return {
            errorMessage: resData.data,
            status: response.status
        }
    } else {
        const resData = await response.json();
        
        setSystemSettings(url, resData.last_global_sync, resData) 
        setNewUserPermissions(url, resData.last_permission_update, dispatch)

        if (dispatch) {
            dispatch(caseActions.batchUpdate(resData.case_data))
        } 

        return resData.data
    }
}

// START user

export const userLogin = async (body) => {
    const data = await genericFetch(
        `${API_ROUTE}/user/login`,
        'POST',
        body
    )

    return data
}

export const userForgaotpassword = async (body) => {
    const data = await genericFetch(
        `${API_ROUTE}/user/forgot-password`,
        'POST',
        body
    )

    return data
}

export const userVerifyOTP = async (body) => {
    const data = await genericFetch(
        `${API_ROUTE}/user/verify-otp`,
        'POST',
        body
    )

    return data
}


export const getUser = async (token) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/user/me`,
        null,
        null
    )

    return data
}

export const getAllUsers = async (token) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/users`,
        null,
        null
    )

    return data
}

export const getRecentCases = async (token) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/recent-cases`,
        null,
        null
    )

    return data
}

export const getUserWorkQueueViews = async (token) => {
    try {
        const data = await genericFetchBearer(
            token,
            `${API_ROUTE}/user/work-queue-views`,
            null,
            null
        )
        if (!data || data.errorMessage) {
            return { views: {} }
        }
        return data
    } catch (e) {
        return { views: {} }
    }
}

export const saveWorkQueueMyView = async (token, slug, body) => {
    try {
        return await genericFetchBearer(
            token,
            `${API_ROUTE}/user/work-queue-views/${encodeURIComponent(slug)}`,
            'PUT',
            body
        )
    } catch (e) {
        return { errorMessage: 'Could not save your view.' }
    }
}

export const deleteWorkQueueMyView = async (token, slug) => {
    try {
        return await genericFetchBearer(
            token,
            `${API_ROUTE}/user/work-queue-views/${encodeURIComponent(slug)}`,
            'DELETE',
            null
        )
    } catch (e) {
        return { errorMessage: 'Could not clear your saved view.' }
    }
}

// END user

// START Attorneys
export const searchLawFirm = async (token, searchQuery) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/search/law-firms?search=${encodeURIComponent(searchQuery ?? '')}`,
        null,
        null
    )

    return data
}

export const searchLawFirmLocation = async (token, searchQuery) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/search/law-firm-locations?search=${searchQuery}`,
        null,
        null
    )

    return data
}

export const selectLawFirm = async (token, lawFirmId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/search/law-firms/${lawFirmId}`,
        null,
        null
    )

    return data
}

export const createLawFirm = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms`,
        'POST',
        body
    )

    return data
}
export const createLawFirmLocation = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms/locations`,
        'POST',
        body
    )

    return data
}

export const editLawFirmLocation = async (token, locationID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms/locations/${locationID}`,
        'PUT',
        body
    )

    return data
}
export const createLawFirmAttorney = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms/attorneys`,
        'POST',
        body
    )

    return data
}

export const editLawFirmAttorney = async (token, AttorneyID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms/attorneys/${AttorneyID}`,
        'PUT',
        body
    )

    return data
}

export const createLawFirmCaseManager = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms/case-managers`,
        'POST',
        body
    )

    return data
}
export const editLawFirmCaseManager = async (token, CaseManagerID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms/case-managers/${CaseManagerID}`,
        'PUT',
        body
    )

    return data
}


// sub set - negotiators
export const createLawFirmNegotiator = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms/negotiators`,
        'POST',
        body
    )

    return data
}

export const editLawFirmNegotiator = async (token,NegotiatorID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/law-firms/negotiators/${NegotiatorID}`,
        'PUT',
        body
    )

    return data
}

export const createNegotiationPayment = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-negotiation-payment`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const updateNegotiationPayment = async (token, caseId, body, paymentId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/update-negotiation-payment/${paymentId}`,
        'PUT',
        body,
        dispatch
    )

    return data
}

export const updateFullNegotiatorsData = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/case-negotiation`,
        null,
        null
    )

    return data
}

// END Attorneys


// START Cases 
export const getCaseDetails = async (token, caseId, useNG, workQ) => {
    const params = new URLSearchParams()
    if (useNG) {
        params.set('use', 'person_number')
    }
    if (workQ) {
        params.set('workQ', workQ)
    }
    // Backend omits services/surgeries/SOS on case detail unless opted in (CaseRelationEagerLoads::detailLoads).
    params.set('include_services', '1')
    params.set('include_surgeries', '1')
    params.set('include_sos', '1')
    const qs = params.toString()

    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}${qs ? `?${qs}` : ''}`,
        null,
        null
    )

    return data
}

export const getCaseDetailsLogs = async (token, caseId, filters, order, pageNumber) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/logs?page=${pageNumber}${order ? order : ''}${filters ? filters : ''}`,
        null,
        null
    )

    return data
}

export const caseQuickUpdate = async (token, caseId, body, dispatch, isInWorkQ, options = {}) => {
    options = options ?? {}
    const abortKey = options.staleGuardKey ?? null
    const controller = abortKey ? abortQuickUpdateForKey(abortKey) : null

    if (options.staleGuardKey && options.staleGuardSequence != null) {
        latestQuickUpdateSequenceByKey.set(options.staleGuardKey, options.staleGuardSequence)
    }

    let data
    try {
        const query = []
        const workqSlug =
            body?.workq ??
            (typeof isInWorkQ === 'string' && isInWorkQ ? isInWorkQ : null)

        if (isInWorkQ) {
            query.push('in_workq=1')
            if (workqSlug) {
                query.push(`workq_slug=${encodeURIComponent(String(workqSlug))}`)
            }
        }

        const queryString = query.length ? `?${query.join('&')}` : ''

        data = await genericFetchBearer(
            token,
            `${API_ROUTE}/cases/${caseId}/quick-update${queryString}`,
            'PUT',
            body,
            dispatch,
            null,
            controller?.signal ?? null,
            true,
            {
                // Keep quick updates lightweight unless a caller explicitly opts in.
                allowImplicitMerge: options.allowImplicitMerge ?? false,
                quickUpdateAutoMergeCaseData: options.quickUpdateAutoMergeCaseData ?? true,
                quickUpdateMeta: {
                    staleGuardKey: options.staleGuardKey ?? null,
                    staleGuardSequence: options.staleGuardSequence ?? null,
                }
            }
        )
    } finally {
        if (abortKey && controller) {
            releaseQuickUpdateController(abortKey, controller)
        }
    }

    const shouldNotifySaved = options.notifySuccessToast ?? true
    if (shouldNotifySaved && data && !data.errorMessage && !data.staleResponse) {
        store.dispatch(notificationActions.setQuickUpdateSaved())
    }

    return data
}

export const caseQuickUpdateMass = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/quick-update-mass`,
        'PUT',
        body,
        dispatch
    )

    return data
}

export const createSosCharge = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/sos`,
        'POST',
        body,
        dispatch,
        null,
        null,
        true,
        {
            quickUpdateAutoMergeCaseData: true,
        }
    )

    if (data && !data.errorMessage && !data.staleResponse) {
        store.dispatch(notificationActions.setQuickUpdateSaved())
    }

    return data
}

export const createAscReferral = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/asc-referral`,
        'POST',
        body,
        dispatch,
        null,
        null,
        true,
        {
            quickUpdateAutoMergeCaseData: true,
        }
    )

    if (data && !data.errorMessage && !data.staleResponse) {
        store.dispatch(notificationActions.setQuickUpdateSaved())
    }

    return data
}

export const linkSurgeryFromService = async (token, caseId, serviceId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/services/${serviceId}/link-surgery`,
        'POST',
        {},
        dispatch,
        null,
        null,
        true,
        {
            quickUpdateAutoMergeCaseData: true,
        }
    )

    if (data && !data.errorMessage && !data.staleResponse) {
        store.dispatch(notificationActions.setQuickUpdateSaved())
    }

    return data
}

export const workQQuickUpdate = async (token, slug, caseId, body, dispatch, options = {}) => {
    options = options ?? {}
    const abortKey = options.staleGuardKey ?? null
    const controller = abortKey ? abortQuickUpdateForKey(abortKey) : null

    if (options.staleGuardKey && options.staleGuardSequence != null) {
        latestQuickUpdateSequenceByKey.set(options.staleGuardKey, options.staleGuardSequence)
    }

    let result
    try {
        result = await genericFetchBearer(
            token,
            `${API_ROUTE}/work-queues/${slug}/${caseId}/update`,
            'PUT',
            body,
            dispatch,
            null,
            controller?.signal ?? null,
            true,
            {
                allowImplicitMerge: false,
                quickUpdateMeta: {
                    staleGuardKey: options.staleGuardKey ?? null,
                    staleGuardSequence: options.staleGuardSequence ?? null,
                },
            }
        )
    } finally {
        if (abortKey && controller) {
            releaseQuickUpdateController(abortKey, controller)
        }
    }

    if (!result) {
        return null
    }

    // For quick updates, genericFetchBearer returns `{ data, staleResponse, aborted? }`.
    if (typeof result === 'object' && Object.prototype.hasOwnProperty.call(result, 'data')) {
        return result
    }
    return { data: result, staleResponse: false }
}

export const workQGetTotals = async (token, slug, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues/${slug}/totals`,
        'POST',
        body
    )

    return data
}

export const assignCaseAttorney = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/assign-attorney`,
        'POST',
        body
    )

    return data
}

export const acceptNegotiation = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/accept-negotiation`,
        'POST',
        body,
        dispatch
    )

    return data
}



// END Cases

//
// START Appointments
export const getAppointments = async (token, caseId, filters, order, pageNumber) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/appointments?page=${pageNumber}${order ? order : ''}${filters ? filters : ''}`,
        null,
        null
    )

    return data
}

export const getFutureSurgicalAppointments = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/future-surgical-appointments`,
        null,
        null
    )

    return data
}

export const getTransportationAvailableAppointments = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/transportation-available-appointments`,
        null,
        null
    )

    return data
}
// START Insurance

export const generalAsyncSearch = async (token, path, searchQuery, signal = null) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/search/${path}?search=${encodeURIComponent(searchQuery ?? '')}`,
        null,
        null,
        null,
        null,
        signal
    )

    return data
}

export const selectInsurance = async (token, insuranceId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/search/insurance-carriers/${insuranceId}`,
        null,
        null
    )

    return data
}

export const assignCaseLiability = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/assign-liability`,
        'POST',
        body
    )

    return data
}

export const insuranceCarrierCreate = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/insurance-carriers`,
        'POST',
        body
    )

    return data
}

export const insuranceCarrierLocationCreate = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/insurance-carriers/locations`,
        'POST',
        body
    )

    return data
}

export const insuranceCarrierLocationUpdate = async (token, locationID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/insurance-carriers/locations/${locationID}`,
        'PUT',
        body
    )

    return data
}

export const insuranceCarrierAdjusterCreate = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/insurance-carriers/adjusters`,
        'POST',
        body
    )

    return data
}

export const insuranceCarrierAdjusterUpdate = async (token, adjusterID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/insurance-carriers/adjusters/${adjusterID}`,
        'PUT',
        body
    )

    return data
}

// END Insurance


// START MedPay/UM

export const assignCaseMedPayUM = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/assign-mp-pip-um`,
        'POST',
        body
    )

    return data
}

// END MedPay/UM


// START Workers Comp
export const assignCaseWorkersComp = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/assign-worker-comp`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const insuranceCarrierNursenCreate = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/insurance-carriers/nurses`,
        'POST',
        body
    )

    return data
}
export const insuranceCarrierNurseUpdate = async (token, nurseID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/insurance-carriers/nurses/${nurseID}`,
        'PUT',
        body
    )

    return data
}
// END  Workers Comp


// START MajorMed

export const assignCaseMajorMed = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/assign-major-med`,
        'POST',
        body
    )

    return data
}

// END MajorMed


//START Notes
export const createNoteApi = async (token, caseId, body, workqId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-note${workqId ? '?work_queue_id=' + workqId : ''}`,
        'POST',
        body,
        dispatch,
        caseId
    )

    return data
}

export const deleteNoteApi = async (token, caseId, noteId, reason) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/notes/${noteId}`,
        'DELETE',
        { reason }
    )

    return data
}
//END Notes

//START Alerts
export const createAlertApi = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-alert`,
        'POST',
        body
    )

    return data
}
//END Alerts


// START system

export const getSystemSettings = (token, signal) => {
    if (signal) {
        return requestSystemSettings(token, signal)
    }
    if (!inFlightSystemSettingsPromise) {
        inFlightSystemSettingsPromise = requestSystemSettings(token).finally(() => {
            inFlightSystemSettingsPromise = null
        })
    }
    return inFlightSystemSettingsPromise
}

// END system


// START upload documents 
export const uploadDocumentApi = async (token, caseID, body, dispatch) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/upload-document`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const sendCaseToMoonbirdApi = async (token, caseId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/send-to-moonbird`,
        "POST",
        null,
        dispatch
    )

    return data
}

export const updateDocumentApi = async (token, caseID, documentId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/change-document-type/${documentId}`,
        'POST',
        body
    )
    return data
}

export const updateUploadedDocument = async (token, caseID, documentId, body) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/upload-document/${documentId}`,
        'POST',
        body
    )

    return data
}

export const deleteCaseDocumentApi = async (token, caseID, documentId, reason, dispatch, hardDelete = false) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/documents/${documentId}`,
        'DELETE',
        { reason, hard_delete: Boolean(hardDelete) },
        dispatch
    )

    return data
}

export const hardDeleteCaseDocumentApi = async (token, caseID, documentId, reason, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/documents/${documentId}/hard-delete`,
        'POST',
        { reason },
        dispatch
    )

    return data
}

export const restoreCaseDocumentApi = async (token, caseID, documentId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/documents/${documentId}/restore`,
        'POST',
        null,
        dispatch
    )

    return data
}

export const uploadProcodocsDocumentApi = async (token, caseID, body, dispatch) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/upload-procodocs-document`,
        'POST',
        body,
        dispatch
    )

    return data
}
export const updateProcodocsUploadedDocument = async (token, caseID, documentId, body, dispatch) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/upload-procodocs-document/${documentId}`,
        'POST',
        body,
        dispatch
    )

    return data
}
export const markProcoDocumentAsVerified = async (token, caseID, documentId) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/mark-verified-procodocs-document/${documentId}`,
        'POST',
        null
    )

    return data
}
export const getViewDownloadLinkForProcoDocs = async (token, caseID, type, documentId, dispatch) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/${type}/view-link/${documentId}`,
        null,
        null,
        dispatch
    )

    return data
}
export const lockUnlockProcoDocuments = async (token, caseID, documentId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/lock-unlock-ecm-procodocs-document/${documentId}`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const deleteProcoDocsDocumentApi = async (token, caseID, documentId, reason, dispatch, hardDelete = false) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/procodocs-documents/${documentId}`,
        'DELETE',
        { reason, hard_delete: Boolean(hardDelete) },
        dispatch
    )

    return data
}

export const hardDeleteProcoDocsDocumentApi = async (token, caseID, documentId, reason, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/procodocs-documents/${documentId}/hard-delete`,
        'POST',
        { reason },
        dispatch
    )

    return data
}

export const restoreProcoDocsDocumentApi = async (token, caseID, documentId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/procodocs-documents/${documentId}/restore`,
        'POST',
        null,
        dispatch
    )

    return data
}

export const getShareableLinkForDocs = async (token, caseID, type, documentId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/${type}/share-link/${documentId}`,
        null,
        null
    )

    return data
}

export const uploadRecordsDocumentApi = async (token, caseID, body, dispatch) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/upload-record-document`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const recordsSharedWithApi = async (token, caseId, case_practice_id, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/upload-record-via/${case_practice_id}`,
        'POST',
        body,
        dispatch
    )

    return data
}


// END upload documents 

export const getDownloadPDFWithUrl = async (token, url) => {
    const data = await genericFileFetch(
        token,
        url,
        null,
        null
    )

    return data
}



//START  related patients 
export const getCaseRelatedPatients = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/patient-search`,
        null,
        null
    )

    return data
}


export const searchPatientByName = async (token, patientName) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/patient-search?search=${patientName}`,
        null,
        null
    )

    return data
}

export const flexibleSearchPatient = async (token, searchParam,searchTerm) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/patient-search${searchParam}${searchTerm}`,
        null,
        null
    )

    return data
}

export const flexibleSearchPatientCompound = async (token, searchPString) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/patient-search?${searchPString}`,
        null,
        null
    )

    return data
}

export const addRelatedPatient = async (token, caseID, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/add-related-patient`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const removeRelatedPatient = async (token, caseID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/remove-related-patient`,
        'POST',
        body
    )

    return data
}

export const removeSugestedRelatedPatient = async (token, caseID, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/remove-occupant-suggestion`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const getCaseRelatedPatientsCHUSA = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/chusa-patient-search`,
        null,
        null
    )

    return data
}

export const addRelatedCHUSAPatient = async (token, caseID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/add-chusa-related-patient`,
        'POST',
        body
    )

    return data
}

export const removeRelatedCHUSAPatient = async (token, caseID, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/remove-chusa-related-patient`,
        'POST',
        body
    )

    return data
}
// END related patients






// transportation

export const addTransportationRide = async (token, caseID, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/case-transportation-ride`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const getTransportationManualAppointments = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/transportation-manual-appointments`,
        null,
        null
    )

    return data
}

export const previewManualTransportationRide = async (token, caseID, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/case-transportation-ride/manual/preview`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const addManualTransportationRide = async (token, caseID, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/case-transportation-ride/manual`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const addConciergeTransportationRide = async (token, caseID, body, dispatch) => {
    return addManualTransportationRide(token, caseID, body, dispatch)
}


export const deleteTransportationRide = async (token, caseID, rideId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/case-transportation-ride/${rideId}`,
        'DELETE',
        null,
        dispatch
    )

    return data
}

export const deleteCaseSurgery = async (token, caseID, surgeryId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/case-surgeries/${surgeryId}`,
        'DELETE',
        null,
        dispatch
    )

    return data
}

export const updateTransportationRide = async (token, caseID, rideId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseID}/case-transportation-ride/${rideId}`,
        'PUT',
        body
    )

    return data
}



// START Tasks api 
// export const getTasks = async (token) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/tasks`,
//         null,
//         null
//     )

//     return data
// }
export const getTasks = async (token, filters, order, pageNumber) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/tasks?page=${pageNumber}${order ? order : ''}${filters ? filters : ''}`,
        null,
        null
    )

    return data
}

export const exportTasks = async (token, filters, order) => {
    let query = ''
    if (order) {
        query += order.replace(/^&/, '')
    }
    if (filters) {
        query += filters
    }
    if (query.startsWith('&')) {
        query = query.slice(1)
    }

    const data = await genericFileFetch(
        token,
        `${API_ROUTE}/tasks/export${query ? `?${query}` : ''}`,
        'GET',
        null
    )

    return data
}

export const getTasksFilters = async (token, filter) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/tasks${filter}`,
        null,
        null
    )

    return data
}

export const getTasksSummary = async (token) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/tasks/summary`,
        null,
        null
    )

    return data
}


export const resolveTask = async (token, taskId, body, workqId, dispatch, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/tasks/${taskId}/resolve${workqId ? '?work_queue_id=' + workqId : ''}`,
        'POST',
        body,
        dispatch,
        caseId
    )

    return data
}

export const starTask = async (token, taskId, body, workqId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/tasks/${taskId}/starred`,
        'POST',
        body
    )

    return data
}

export const resolveAlert = async (token, caseId, alertId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/resolve-alert/${alertId}`,
        'POST',
        body
    )

    return data
}


// export const getTaksByPage = async (token, pageNumber) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/tasks?page=${pageNumber}`,
//         null,
//         null
//     )

//     return data
// }


// export const getTaksByPageWithPreviousFilters = async (token, filters, pageNumber) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/tasks?${filters}&page=${pageNumber}`,
//         null,
//         null
//     )

//     return data
// }
// END Tasks api 


// Work Queues 

// export const getPatientsWorkQueuesFilter = async (token, searchParams) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/work-queues/patients?${searchParams}`, // year&case_type_id=&case_status_id=1&location_id&appointment_date&missed_appointment_date&search
//         null,
//         null
//     )

//     return data
// }

// export const getPatientsByPageWithPreviousFilters = async (token, filters, pageNumber) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/work-queues/patients?${filters}&page=${pageNumber}`,
//         null,
//         null
//     )

//     return data
// }

// export const getWorkQBySlug = async (token, slug, pageNumber) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/work-queues/${slug}?page=${pageNumber}`,
//         null,
//         null
//     )

//     return data
// }

// export const getWorkQBySlugWithPreviousFilters = async (token, slug, pageNumber, filters) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/work-queues/${slug}?${filters}&page=${pageNumber}`,
//         null,
//         null
//     )

//     return data
// }

export const getWorkQBySlugOrdered = async (token, slug, orderBy) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues/${slug}?${orderBy}`,
        null,
        null
    )

    return data
}

// export const getWorkQBySlugMaster = async (token, slug, pageNumber, filters, order) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/work-queues/${slug}?page=${pageNumber}${filters ? filters : ''}${order ? '&'+order : ''}`,
//         null,
//         null
//     )

//     return data
// }

export const getWorkQBySlugMaster = async (token, slug, body, signal) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues/${slug}`,
        'POST',
        body,
        null,
        null,
        signal
    )

    return data
}

export const getWorkQDrwaer = async (token, slug, caseId, useNG = false) => {
    const params = new URLSearchParams()
    if (useNG) {
        params.set('use', 'person_number')
    }
    params.set('include_services', '1')
    params.set('include_surgeries', '1')
    params.set('include_sos', '1')
    const qs = params.toString()

    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues/${slug}/${caseId}?${qs}`,
        null,
        null
    )

    return data
}

export const getCollectionsForWorkQFilters = async (token, role, search) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/users?role=${role}&search=${search}`, //?search&role=${role}
        null,
        null
    )

    return data
}

// export const getGlobalWorkQ = async (token, slug, pageNumber, filters, order) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/${slug}?page=${pageNumber}${filters ? filters : ''}${order ? '&'+order : ''}`,
//         null,
//         null
//     )

//     return data
// }

export const getGlobalWorkQ = async (token, slug, body, signal) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/${slug}`,
        'POST',
        body,
        null,
        null,
        signal
    )

    return data
}

export const exportStandardWorkQ  = async (token, slug, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues/${slug}/export`,
        'POST',
        body,
    )

    return data
}

export const exportWorkQStatusReport = async (token) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues/reports/queue-status`,
        'POST',
        null,
    )

    return data
}


export const invalidFaxDetailsApi = async (token, lawFirmId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues-invalid-fax/${lawFirmId}`,
        null,
        null
    )

    return data
}

export const saveInvalidFaxApi = async (token, type, id, fax) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues-invalid-fax/${type}/${id}`,
        'POST',
        { fax }
    )

    return data
}

export const policeReportsDetailsApi = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues-police-reports/${caseId}`,
        null,
        null
    )

    return data
}

export const mismatchedDataDetailsApi = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues-mismatched/${caseId}`,
        null,
        null
    )

    return data
}

export const workQCallFollowupUpdate = async (token, appointment_id, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues-call-follow-ups/${appointment_id}`,
        'PUT',
        body,
        dispatch
    )
    return data
}

// export const getMedicalRecordsQ = async (token, slug, pageNumber, filters, order) => {
//     const data = await genericFetchBearer(
//         token,
//         `${API_ROUTE}/${slug}?page=${pageNumber}${filters ? filters : ''}${order ? '&'+order : ''}`,
//         null,
//         null
//     )

//     return data
// }

export const sendMailApi = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/send-mail`,
        'POST',
        body
    )

    return data
}

export const sendSMSApi = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/send-sms`,
        'POST',
        body
    )

    return data
}


export const getMajorMedFromPolicy = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/get-major-med-from-policy`,
        'POST',
        body
    )

    return data
}



// ########### Start CHAT ############# 
export const chatReadMessage = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/case-chat/read`,
        null,
        null
    )

    return data
}

export const chatSendMessage = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/case-chat/send-message`,
        'POST',
        body
    )

    return data
}

export const chatSyncMessages = async (token, caseId, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/case-chat`,
        null,
        null,
        dispatch
    )

    return data
}
// ########### End CHAT ############# 


// ####################### Start LIEN ######################## 
export const createLien = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-lien`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const auditCompleteCase = async (token, caseId, dispatch) => {
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...authorizationHeaders(token),
        },
        body: JSON.stringify({}),
    }

    let response
    try {
        response = await fetch(
            `${API_ROUTE}/cases/${caseId}/audit-complete`,
            options
        )
    } catch {
        return { errorMessage: 'Something went wrong' }
    }

    const resData = await parseResponseJson(response)

    if (!response.ok) {
        if (response.status === 401) {
            return handleUnauthorizedResponse(response)
        }
        const payload =
            resData && typeof resData === 'object' && resData.data != null && typeof resData.data === 'object'
                ? resData.data
                : resData
        return {
            ...(payload && typeof payload === 'object' ? payload : {}),
            errorMessage: payload?.errors ?? payload?.errorMessage ?? resData?.errors ?? 'Something went wrong',
            status: response.status,
        }
    }

    if (resData?.case_data && typeof dispatch === 'function') {
        dispatch(caseActions.batchUpdate(resData.case_data))
    }

    // AttachGlobalData wraps message/practice_results under `data` and lifts case_data to the top.
    const inner =
        resData && typeof resData === 'object' && resData.data != null && typeof resData.data === 'object'
            ? resData.data
            : resData
    if (!inner || typeof inner !== 'object') {
        return { message: 'Audit Complete saved.' }
    }

    return {
        ...inner,
        ...(resData?.case_data != null ? { case_data: resData.case_data } : {}),
        ...(resData?.work_queue_data != null ? { work_queue_data: resData.work_queue_data } : {}),
    }
}

export const recreateLien = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/recreate-lien`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const uploadRecordedLienDocument = async (token, caseId, body, dispatch) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/upload-recorded-lien-document`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const parseLienDocument = async (token, caseId, body) => {
    const data = await genericFetchMultipartBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/parse-lien-document`,
        'POST',
        body
    )

    return data
}

export const createLienFromDocument = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-lien-from-document`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const fetchLienStatus = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/fetch-lien-status`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const createSatisfactionLetter = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-satisfaction-letter`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const validateCreateLien = async (token, caseId, casePracticeID, slug, body) => {
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...authorizationHeaders(token),
        },
    }

    if (body) {
        options.body = JSON.stringify(body)
    }

    let response
    try {
        response = await fetch(
            `${API_ROUTE}/cases/${caseId}/${slug}/${casePracticeID}`,
            options
        )
    } catch {
        return { errorMessage: 'Something went wrong' }
    }

    const resData = await parseResponseJson(response)
    const payload =
        resData && typeof resData === 'object' && resData.data != null && typeof resData.data === 'object'
            ? resData.data
            : resData

    if (!response.ok) {
        if (response.status === 401) {
            return handleUnauthorizedResponse(response)
        }

        if (payload && typeof payload === 'object') {
            return { ...payload, status: response.status }
        }

        return {
            errorMessage: 'Something went wrong',
            status: response.status,
        }
    }

    return payload && typeof payload === 'object' ? payload : {}
}
export const createOutsideLien = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-outside-lien`,
        'POST',
        body
    )

    return data
}
// ####################### End LIEN ######################## 


// ####################### Start REFERRALS ######################## 
export const getReferralsApi = async (token, filters, order, pageNumber) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/patient-referrals?page=${pageNumber}${order ? order : ''}${filters ? filters : ''}`,
        null,
        null
    )

    return data
}

export const updatePatientReferralApi = async (token, referralId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/patient-referrals/${referralId}/status`,
        'PATCH',
        body,
        dispatch
    )

    return data
}
// ####################### End REFERRALS ######################## 


// ####################### Start History all ######################## 
export const getAllCaseLogs = async (token, filters, order, pageNumber) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/logs?page=${pageNumber}${order ? order : ''}${filters ? filters : ''}`,
        null,
        null
    )

    return data
}

export const exportHistory = async (token, filters) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/logs/export${filters ? '?' + filters : ''}`,
        null,
        null
    )
    return data
}
// ####################### End History all ######################## 

export const getCaseLienHistory = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/lien-history`,
        null,
        null
    )

    return data
}

// ####################### Start TRANSFERS ######################## 
export const getTransfersApi = async (token, filters, order, pageNumber) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/transfers?page=${pageNumber}${order ? order : ''}${filters ? filters : ''}`,
        null,
        null
    )

    return data
}

export const createTransferRequest = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/transfer`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const cancelTransferRequest = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/cancel-transfer`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const completeTransferRequest = async (token, caseId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/complete-transfer`,
        'POST',
        body,
        dispatch
    )

    return data
}

export const updateTransferRequest = async (token, caseId, transferId, body, dispatch) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/transfer/${transferId}`,
        'POST',
        body,
        dispatch
    )

    return data
}
// ####################### End REFERRALS ######################## 


// ####################### Start exports all ######################## 
export const getExportsLogs = async (token, filters, order, pageNumber) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues/export/logs?page=${pageNumber}${order ? order : ''}${filters ? filters : ''}`,
        null,
        null
    )

    return data
}

export const getExportDownloadLink = async (token, logId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/work-queues/export/logs/${logId}/download-link`,
        null,
        null
    )

    return data
}
// ####################### End exports all ######################## 


// ############################ MISC ###################################### 
export const resetDataForCase = async (token, caseId, type) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/reset-patient-data/${caseId}${type ? '?reset_type=' + type : ''}`,
        null,
        null
    )

    return data
}

export const syncEntireCase = async (token, caseId) => {
    // the patient must have a person number!!
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/sync-entrie-case`,
        null,
        null
    )

    return data
}


export const pullFlagsForCaseDetails = async (token, caseId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/case-flags`,
        null,
        null
    )

    return data
}


export const getPostalInformation = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/postal-information`,
        'POST',
        body
    )

    return data
}

export const resolveCountyFromAddress = async (token, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/resolve-county-from-address`,
        'POST',
        body
    )

    return data
}

export const generateCaseSummaryPDF = async (token, caseId) => {
    const data = await genericFileFetch(
        token,
        `${API_ROUTE}/cases/${caseId}/case-summary-pdf`,
        null,
        null
    )

    return data
}

export const generateGlobalDownloadPDF = async (token, caseId, url) => {
    const data = await genericFileFetch(
        token,
        `${API_ROUTE}/cases/${caseId}/${url}`,
        null,
        null
    )

    return data
}

// pdf print 

export const generatePDFForPrint = async (body, caseId, additionalHeader) => {
    const data = await genericFetch(
        `${API_ROUTE}/cases-pdf/${caseId}`,
        null,
        body,
        additionalHeader
    )

    return data
}

export const downloadPDFVariant = async (token, caseId, url) => {
    // slug, type, typeID
    const data = await genericFileFetch(
        token,
        `${API_ROUTE}/cases/${caseId}/download-pdf/${url.slug}/${url.type}${url.typeID ? '/' + url.typeID : ''}`,
        null,
        null,
    )

    return data
}


// System alerts 

export const getSystemAlerts = async (token) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/system-alerts`,
        null,
        null
    )

    return data
}


export const dismissSystemAlert = async (token, alertID) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/system-alerts/dismiss/${alertID}`,
        "POST",
        null
    )

    return data
}

export const getReleaseNotes = async (token, search = '', pageNumber = 1, startDate = '', endDate = '') => {
    const params = new URLSearchParams()
    params.set('page', String(pageNumber || 1))
    params.set('per_page', '20')
    if (search) {
        params.set('search', search)
    }
    if (startDate) {
        params.set('start_date', startDate)
    }
    if (endDate) {
        params.set('end_date', endDate)
    }

    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/release-notes?${params.toString()}`,
        null,
        null
    )

    return data
}


// ############################ Start PROCODOCS REQUESTS ######################## 
export const getProcodocsRequests = async (token, caseId, attorneyId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/get-procodocs-requests${attorneyId ? '?case_attorney_id=' + attorneyId : ''}`,
        null,
        null
    )

    return data
}

export const createProcodocsRequest = async (token, caseId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-procodocs-request`,
        'POST',
        body
    )

    return data
}

export const updateProcodocsRequest = async (token, caseId, requestId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/update-procodocs-request/${requestId}`,
        'POST',
        body
    )

    return data
}

export const procodocsRequestSendMessage = async (token, caseId, requestId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/create-procodocs-request-message/${requestId}`,
        'POST',
        body
    )

    return data
}

export const getProcodocsRequestMessages = async (token, caseId, requestId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/get-procodocs-request-messages/${requestId}`,
        null,
        null
    )

    return data
}

export const markProcodocsRequestMessagesAsRead = async (token, caseId, requestId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/mark-procodocs-request-messages-as-read/${requestId}`,
        'POST',
        null
    )

    return data
}

export const resolveProcodocsRequest = async (token, caseId, requestId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/resolve-procodocs-request/${requestId}`,
        'POST',
        null
    )

    return data
}

export const unresolveProcodocsRequest = async (token, caseId, requestId) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/unresolve-procodocs-request/${requestId}`,
        'POST',
        null
    )

    return data
}
// ############################ End PROCODOCS REQUESTS ######################## 



// ############################ Start VERIFICATIONS ######################## 
export const addVerificationLiability = async (token, caseId, liabilityId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/post-liability-verification-history/${liabilityId}`,
        'POST',
        body
    )

    return data
}

export const addVerificationMajorMed = async (token, caseId, majorMedId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/post-major-med-verification-history/${majorMedId}`,
        'POST',
        body
    )

    return data
}

export const addVerificationMedPayPipUm = async (token, caseId, medPayPipUmId, body) => {
    const data = await genericFetchBearer(
        token,
        `${API_ROUTE}/cases/${caseId}/post-med-pay-pip-um-verification-history/${medPayPipUmId}`,
        'POST',
        body
    )

    return data
}
// ############################ End VERIFICATIONS ######################## 

// ############################ Start GDQ REPORTS ######################## 
export const exportGDQReport = async (token, params = null) => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : ''
    const data = await genericFileFetch(
        token,
        `${API_ROUTE}/work-queues/reports/gdq${queryString}`,
        'POST',
        null
    )
    return data
}
// ############################ End GDQ REPORTS ######################## 